/**
 * PetAvatar — PNG-based LearnPet with framer-motion animations + CSS filter moods
 *
 * Props:
 *   petType  : 'elephant' | 'squirrel' | 'peacock' | 'bear' | 'parrot' | 'tiger'
 *   stage    : 1–5  (evolution stage, reserved for future badge overlays)
 *   mood     : 'happy' | 'excited' | 'thinking' | 'celebrating' | 'listening' |
 *              'encouraging' | 'lonely' | 'sleeping'
 *   size     : 'sm'(32) | 'md'(64) | 'lg'(120) | 'xl'(180) | number (px)
 *   animated : boolean
 *   onTap    : function
 */

import { motion } from 'framer-motion';

// ── Size map ──────────────────────────────────────────────────────────────────
const SIZE_MAP = { sm: 32, md: 64, lg: 120, xl: 180 };

// Legacy pet type aliases → canonical PNG names
const PET_ALIAS = {
  panda:   'bear',
  bunny:   'squirrel',
  puppy:   'tiger',
  dolphin: 'peacock',
  kitten:  'parrot',
};

// ── Mood → framer-motion animate + transition ─────────────────────────────────
const MOOD_MOTION = {
  happy: {
    animate: { y: [0, -12, -4, 0] },
    transition: { duration: 1.6, repeat: Infinity, ease: 'easeInOut', repeatDelay: 0.2 },
  },
  excited: {
    animate: { x: [-2, 2, -2, 2, 0] },
    transition: { duration: 0.4, repeat: Infinity, ease: 'linear' },
  },
  thinking: {
    animate: { rotate: [0, 8, 0] },
    transition: { duration: 3, repeat: Infinity, ease: 'easeInOut' },
  },
  celebrating: {
    animate: { y: [0, -20, 0], rotate: [0, -5, 5, 0] },
    transition: { type: 'spring', stiffness: 200, damping: 8, repeat: Infinity, repeatDelay: 0.3 },
  },
  listening: {
    animate: { rotate: [-3, 3] },
    transition: { duration: 3, repeat: Infinity, ease: 'easeInOut', repeatType: 'reverse' },
  },
  encouraging: {
    animate: { rotate: -5, y: -3, scale: 1.05 },
    transition: { duration: 2, repeat: Infinity, ease: 'easeInOut', repeatType: 'reverse' },
  },
  lonely: {
    animate: { scale: [1, 0.92, 1] },
    transition: { duration: 4, repeat: Infinity, ease: 'easeInOut' },
  },
  sleeping: {
    animate: { scale: [1, 1.04, 1] },
    transition: { duration: 3, repeat: Infinity, ease: 'easeInOut' },
  },
  idle: {
    animate: { y: [0, -4, 0] },
    transition: { duration: 2.5, repeat: Infinity, ease: 'easeInOut' },
  },
};

// ── Mood → CSS filter ─────────────────────────────────────────────────────────
const MOOD_FILTER = {
  happy:       'brightness(1.1)',
  excited:     'brightness(1.15)',
  thinking:    'grayscale(0.1)',
  celebrating: 'brightness(1.2)',
  listening:   'brightness(1.0)',
  encouraging: 'brightness(1.05)',
  lonely:      'brightness(0.85) saturate(0.8)',
  sleeping:    'brightness(0.7) saturate(0.6)',
  idle:        'brightness(1.0)',
};

// ── Mood → opacity override ───────────────────────────────────────────────────
const MOOD_OPACITY = {
  listening: 0.95,
  lonely:    0.85,
};

// Per-pet-type tap micro-interactions (one-shot, not looping)
const PET_TAP_ANIM = {
  elephant: { animate: { rotate: [0, -8, 8, -4, 0] }, transition: { duration: 0.6, ease: 'easeInOut' } },
  squirrel: { animate: { scale: [1, 0.8, 1.15, 1] },  transition: { duration: 0.5, ease: 'easeInOut' } },
  peacock:  { animate: { scaleX: [1, 1.3, 1] },        transition: { duration: 0.5, ease: 'easeInOut' } },
  bear:     { animate: { y: [0, -5, 0] },              transition: { type: 'spring', stiffness: 80, damping: 6 } },
  parrot:   { animate: { rotate: [0, 15, -15, 0] },    transition: { duration: 0.5, ease: 'easeInOut' } },
  tiger:    { animate: { scale: [1, 1.12, 1] },        transition: { type: 'spring', stiffness: 400, damping: 10 } },
};

// ── Main export ───────────────────────────────────────────────────────────────
export const PetAvatar = ({
  petType   = 'elephant',
  stage     = 1,
  mood      = 'happy',
  size      = 'md',
  animated  = true,
  onTap,
}) => {
  const px          = typeof size === 'number' ? size : (SIZE_MAP[size] ?? 64);
  const canonical   = PET_ALIAS[petType] || petType;
  const tapKey      = PET_TAP_ANIM[canonical] ? canonical : 'elephant';
  const moodMotion  = MOOD_MOTION[mood] ?? MOOD_MOTION.idle;
  const tapMotion   = PET_TAP_ANIM[tapKey];
  const imgFilter   = MOOD_FILTER[mood] ?? 'brightness(1.0)';
  const imgOpacity  = MOOD_OPACITY[mood] ?? 1;

  const animateTarget = animated ? moodMotion.animate : {};
  const transition    = animated ? moodMotion.transition : {};

  return (
    <motion.div
      animate={animateTarget}
      transition={transition}
      whileTap={onTap ? { ...tapMotion.animate, transition: tapMotion.transition } : { scale: 0.93 }}
      onClick={onTap}
      style={{
        width: px,
        height: px,
        display: 'inline-block',
        position: 'relative',
        cursor: onTap ? 'pointer' : 'default',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        transformOrigin: '50% 50%',
        flexShrink: 0,
      }}
    >
      <img
        src={`/pets/${canonical}.png`}
        alt={canonical}
        draggable={false}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          filter: imgFilter,
          opacity: imgOpacity,
          display: 'block',
        }}
      />

      {/* Sleeping ZZZ overlay */}
      {mood === 'sleeping' && (
        <motion.span
          animate={{ opacity: [0, 1, 0], y: [0, -12, -20] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: 'easeOut', repeatDelay: 0.5 }}
          style={{
            position: 'absolute',
            top: '8%',
            right: '4%',
            fontSize: Math.max(9, px * 0.13),
            fontWeight: 600,
            color: '#A0A0C8',
            pointerEvents: 'none',
            lineHeight: 1,
          }}
        >
          ZZZ
        </motion.span>
      )}

      {/* Stage 4: blue glow ring */}
      {stage === 4 && (
        <div style={{
          position: 'absolute', inset: -3,
          borderRadius: '50%',
          boxShadow: '0 0 12px 4px rgba(120,180,255,0.55)',
          pointerEvents: 'none',
        }} />
      )}

      {/* Stage 5: gold glow ring */}
      {stage >= 5 && (
        <div style={{
          position: 'absolute', inset: -4,
          borderRadius: '50%',
          boxShadow: '0 0 18px 6px rgba(255,215,0,0.65)',
          pointerEvents: 'none',
        }} />
      )}
    </motion.div>
  );
};

export default PetAvatar;
